import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import LabHeader from './LabHeader';

/**
 * PipelineTraceTab
 *
 * Enter any query and see exactly what the backend did:
 *   - How it was classified (league, team, market, sportsbook, bet type)
 *   - Which pipeline path it took (queryCachedSGPs vs nlOdds fast-paths vs live-gen)
 *   - The full _debug_path trace (source, sort stack, filter stack, item counts)
 *   - Top-5 result preview with raw EV fields (ev_percent, ev_percent_clean, fair_probability)
 *   - sgp_cache_index coverage for the detected league
 *
 * Calls nlOdds directly so the trace reflects the exact same path a real user query takes.
 */

const FUNCTIONS_URL = typeof __DEV__ !== 'undefined' && __DEV__
  ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
  : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const SUGGESTED_QUERIES = [
  'fanduel nba sgps',
  'mlb dk bets',
  'nhl sgps',
  'warriors sgps',
  'caesars nfl sgps',
  'wnba best bets',
  'nba moneyline',
  'mlb player props',
];

export default function PipelineTraceTab({ embedded = false }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ classification: true, path: true, results: false, raw: false });
  const inputRef = useRef(null);

  const toggleSection = (key) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const runTrace = async (q) => {
    const prompt = (q || query).trim();
    if (!prompt) return;
    setLoading(true);
    setTrace(null);
    setError(null);

    const start = Date.now();
    try {
      const res = await fetch(`${FUNCTIONS_URL}/nlOdds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, _traceMode: true }),
      });
      const json = await res.json();
      const elapsed = Date.now() - start;

      const arr = Array.isArray(json?.results) ? json.results : [];
      const items = arr.flatMap(r => Array.isArray(r?.items) ? r.items : []).flatMap(x => Array.isArray(x?.odds) ? x.odds : [x]);

      // Extract _debug_path — may be on top-level or nested in results[0]
      const debugPath = json?._debug_path || arr[0]?._debug_path || null;
      const diagnostics = json?._diagnostics || null;

      // Build index coverage if we have a detected league
      let indexHealth = null;
      const detectedLeague = json?._league || json?.league || json?._queryClassification?.entities?.league;
      if (detectedLeague) {
        try {
          const idxRes = await fetch(`${FUNCTIONS_URL}/getSGPCacheAggregateStats`);
          const idxData = await idxRes.json();
          const byLeague = idxData?.by_league || {};
          indexHealth = byLeague[detectedLeague.toLowerCase()] || null;
        } catch (_) {}
      }

      setTrace({
        prompt,
        elapsed,
        status: res.status,
        classification: json?._queryClassification || null,
        detectedLeague,
        detectedTeam: json?._detectedTeam || json?._homeTeam || null,
        detectedSportsbook: json?._preferredSportsbook || null,
        metaSource: json?.meta?.source || json?.source || null,
        cached: json?.meta?.cached ?? json?.cached ?? null,
        cacheAge: json?.meta?.cache_age_seconds ?? null,
        debugPath,
        diagnostics,
        itemCount: items.length,
        topItems: items.slice(0, 5),
        indexHealth,
        rawJson: json,
      });
    } catch (e) {
      setError(e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
      {embedded ? (
        <LabHeader
          icon="chatbubbles-outline"
          title="NLP Lab"
          question="What did we think this query meant, and which path served it?"
          how="Type a query or tap a chip, then Trace. Classification and pipeline path are the point. Display/sort lives in Results Lab."
        />
      ) : (
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Pipeline Trace</Text>
          <Text style={styles.headerSub}>Run any query and inspect the full execution path</Text>
        </View>
      )}

      {/* Query Input */}
      <View style={styles.inputCard}>
        <TextInput
          ref={inputRef}
          style={styles.input}
          value={query}
          onChangeText={setQuery}
          placeholder="e.g. fanduel nba sgps"
          placeholderTextColor="#6B7280"
          onSubmitEditing={() => runTrace()}
          returnKeyType="search"
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TouchableOpacity
          style={[styles.runButton, loading && { opacity: 0.5 }]}
          onPress={() => runTrace()}
          disabled={loading || !query.trim()}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.runButtonText}>Trace</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Suggestions */}
      {!trace && !loading && (
        <View style={styles.suggestionsRow}>
          {SUGGESTED_QUERIES.map(s => (
            <TouchableOpacity
              key={s}
              style={styles.suggestionChip}
              onPress={() => { setQuery(s); runTrace(s); }}
            >
              <Text style={styles.suggestionText}>{s}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Error */}
      {error && (
        <View style={styles.errorCard}>
          <Text style={styles.errorText}>⚠️ {error}</Text>
        </View>
      )}

      {/* Trace Results */}
      {trace && (
        <View style={{ gap: 10 }}>
          {/* Summary Bar */}
          <View style={styles.summaryBar}>
            <SummaryPill label="Status" value={`HTTP ${trace.status}`} color={trace.status === 200 ? '#10B981' : '#EF4444'} />
            <SummaryPill label="Time" value={`${trace.elapsed}ms`} color="#3B82F6" />
            <SummaryPill label="Items" value={String(trace.itemCount)} color="#8B5CF6" />
            <SummaryPill label="Source" value={trace.metaSource || '—'} color={trace.cached ? '#10B981' : '#F59E0B'} />
            {trace.cached !== null && (
              <SummaryPill label="Cache" value={trace.cached ? `hit${trace.cacheAge != null ? ` (${Math.round(trace.cacheAge / 60)}m)` : ''}` : 'miss'} color={trace.cached ? '#10B981' : '#F59E0B'} />
            )}
          </View>

          {/* Query Classification */}
          <CollapsibleCard
            title="Query Classification"
            icon="🏷️"
            expanded={expandedSections.classification}
            onToggle={() => toggleSection('classification')}
          >
            {trace.classification ? (
              <ClassificationDisplay classification={trace.classification} detectedSportsbook={trace.detectedSportsbook} />
            ) : (
              <Text style={styles.emptyText}>No classification data returned</Text>
            )}
          </CollapsibleCard>

          {/* Pipeline Path */}
          <CollapsibleCard
            title="Pipeline Path"
            icon="🛤️"
            expanded={expandedSections.path}
            onToggle={() => toggleSection('path')}
          >
            <PathDisplay trace={trace} />
          </CollapsibleCard>

          {/* Top Results */}
          <CollapsibleCard
            title={`Top Results (${trace.itemCount} total)`}
            icon="📊"
            expanded={expandedSections.results}
            onToggle={() => toggleSection('results')}
          >
            {trace.topItems.length > 0 ? (
              trace.topItems.map((item, i) => <ResultRow key={i} index={i + 1} item={item} />)
            ) : (
              <Text style={styles.emptyText}>No items returned</Text>
            )}
          </CollapsibleCard>

          {/* Raw _debug_path */}
          <CollapsibleCard
            title="_debug_path trace"
            icon="🧪"
            expanded={expandedSections.raw}
            onToggle={() => toggleSection('raw')}
          >
            {trace.debugPath ? (
              <View style={styles.jsonBlock}>
                <Text style={styles.jsonText}>{JSON.stringify(trace.debugPath, null, 2)}</Text>
              </View>
            ) : (
              <View>
                <Text style={styles.emptyText}>No _debug_path in response.</Text>
                <Text style={[styles.emptyText, { marginTop: 4, fontSize: 11 }]}>
                  This means the query went through queryCachedSGPs (index.js) rather than a fast-path
                  in sgpCache.js. Check the raw source/meta fields above to confirm.
                </Text>
              </View>
            )}
          </CollapsibleCard>
        </View>
      )}
    </ScrollView>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SummaryPill({ label, value, color }) {
  return (
    <View style={[styles.summaryPill, { borderColor: color + '55' }]}>
      <Text style={[styles.summaryLabel]}>{label}</Text>
      <Text style={[styles.summaryValue, { color }]}>{value}</Text>
    </View>
  );
}

function CollapsibleCard({ title, icon, expanded, onToggle, children }) {
  return (
    <View style={styles.card}>
      <TouchableOpacity style={styles.cardHeader} onPress={onToggle} activeOpacity={0.7}>
        <Text style={styles.cardTitle}>{icon} {title}</Text>
        <Text style={styles.chevron}>{expanded ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {expanded && <View style={styles.cardBody}>{children}</View>}
    </View>
  );
}

function ClassificationDisplay({ classification, detectedSportsbook }) {
  const ent = classification?.entities || {};
  const rows = [
    { label: 'Bet Type', value: classification?.betType || classification?.type },
    { label: 'League', value: ent.league },
    { label: 'Team', value: ent.team || ent.homeTeam },
    { label: 'Player', value: ent.player },
    { label: 'Market', value: ent.market },
    { label: 'Sportsbook', value: detectedSportsbook || ent.sportsbook },
    { label: 'Is Live', value: ent.isLive != null ? String(ent.isLive) : null },
    { label: 'Confidence', value: classification?.confidence != null ? `${Math.round(classification.confidence * 100)}%` : null },
  ].filter(r => r.value != null && r.value !== '');

  if (rows.length === 0) return <Text style={styles.emptyText}>Empty entities</Text>;

  return (
    <View style={{ gap: 6 }}>
      {rows.map(r => (
        <View key={r.label} style={styles.kvRow}>
          <Text style={styles.kvLabel}>{r.label}</Text>
          <Text style={styles.kvValue}>{r.value}</Text>
        </View>
      ))}
    </View>
  );
}

function PathDisplay({ trace }) {
  const dp = trace.debugPath;
  const diag = trace.diagnostics;
  const rows = [
    { label: 'Meta Source', value: trace.metaSource, highlight: true },
    { label: 'Sportsbook', value: trace.detectedSportsbook },
    { label: 'League', value: trace.detectedLeague },
    { label: 'Team', value: trace.detectedTeam },
    { label: 'Cache Hit', value: trace.cached != null ? (trace.cached ? '✅ yes' : '❌ miss') : null },
    { label: 'Cache Age', value: trace.cacheAge != null ? `${Math.round(trace.cacheAge / 60)} min` : null },
    ...(dp ? [
      { label: 'Fast Path', value: dp.name || dp.path || dp.stage },
      { label: 'Data Source', value: dp.dataSource || dp.source },
      { label: 'Sort Stack', value: Array.isArray(dp.sortStack) ? dp.sortStack.join(' → ') : dp.sortStack },
      { label: 'Filter Stack', value: Array.isArray(dp.filterStack) ? dp.filterStack.join(', ') : dp.filterStack },
      { label: 'Item Count', value: dp.itemCount != null ? String(dp.itemCount) : null },
      { label: 'Pre-filter Count', value: dp.preFilterCount != null ? String(dp.preFilterCount) : null },
      { label: 'Browse Path', value: dp.browseByEv != null ? (dp.browseByEv ? '🔍 index-based' : '⚠️ recency fallback') : null },
      { label: 'Index Docs', value: dp.indexDocsFound != null ? String(dp.indexDocsFound) : null },
    ] : [
      { label: 'Fast Path', value: '—  (queryCachedSGPs / index.js path)' },
    ]),
    // queryCachedSGPs diagnostics (index.js path)
    ...(diag ? [
      { label: '── Diagnostics ──', value: '', heading: true },
      { label: 'Fixtures Scanned', value: diag.fixturesScanned != null ? String(diag.fixturesScanned) : null },
      { label: 'SGPs (pre-filter)', value: diag.sgpsBeforeApiPricedFilter != null ? String(diag.sgpsBeforeApiPricedFilter) : null },
      { label: 'API-Priced', value: diag.apiPricedCount != null ? String(diag.apiPricedCount) : null },
      { label: 'Max Age', value: diag.maxAgeHours != null ? `${diag.maxAgeHours}h` : null },
    ] : []),
  ].filter(r => r.value != null && r.value !== '' && r.value !== 'undefined');

  if (trace.indexHealth) {
    rows.push({ label: '── Index Health ──', value: '', heading: true });
    const ih = trace.indexHealth;
    if (ih.cached_fixtures != null) rows.push({ label: 'Cached Fixtures', value: String(ih.cached_fixtures) });
    if (ih.total_sgps != null) rows.push({ label: 'Total SGPs', value: String(ih.total_sgps) });
  }

  return (
    <View style={{ gap: 6 }}>
      {rows.map((r, i) => r.heading ? (
        <Text key={i} style={[styles.kvLabel, { marginTop: 8, color: '#6B7280', fontSize: 10 }]}>{r.label}</Text>
      ) : (
        <View key={i} style={styles.kvRow}>
          <Text style={styles.kvLabel}>{r.label}</Text>
          <Text style={[styles.kvValue, r.highlight && { color: '#8B5CF6', fontWeight: '700' }]}>{r.value}</Text>
        </View>
      ))}
    </View>
  );
}

function ResultRow({ index, item }) {
  const evPct = item?.ev_percent ?? item?.ev ?? item?.best_ev;
  const evClean = item?.ev_percent_clean;
  const fp = item?.fair_probability;
  const m1 = item?.ev_method1_additive;
  const m2 = item?.ev_method2_multiplicative;
  const m3 = item?.ev_method3_power;
  const legs = Array.isArray(item?.legs) ? item.legs.length : null;
  const book = item?.sportsbook || item?.book;
  const odds = item?.american_odds ?? item?.odds;

  const evColor = (v) => {
    if (v == null) return '#9CA3AF';
    if (v > 5) return '#10B981';
    if (v > 0) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <View style={styles.resultRow}>
      <Text style={styles.resultIndex}>#{index}</Text>
      <View style={{ flex: 1, gap: 3 }}>
        <View style={styles.resultTopRow}>
          {book && <Text style={styles.resultBook}>{book}</Text>}
          {odds != null && <Text style={styles.resultOdds}>{odds > 0 ? `+${odds}` : odds}</Text>}
          {legs != null && <Text style={styles.resultLegs}>{legs}-leg</Text>}
        </View>
        <View style={styles.evGrid}>
          <EvCell label="EV%" value={evPct} color={evColor(evPct)} />
          <EvCell label="Clean" value={evClean} color={evColor(evClean)} />
          <EvCell label="FairP" value={fp} color="#9CA3AF" suffix="%" />
          <EvCell label="M1" value={m1} color={evColor(m1)} />
          <EvCell label="M2" value={m2} color={evColor(m2)} />
          <EvCell label="M3" value={m3} color={evColor(m3)} />
        </View>
      </View>
    </View>
  );
}

function EvCell({ label, value, color, suffix = '%' }) {
  return (
    <View style={styles.evCell}>
      <Text style={styles.evCellLabel}>{label}</Text>
      <Text style={[styles.evCellValue, { color }]}>
        {value != null ? `${parseFloat(value).toFixed(1)}${suffix}` : '—'}
      </Text>
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0F172A' },
  content: { padding: 20, paddingBottom: 60, gap: 14 },

  header: { marginBottom: 4 },
  headerTitle: { fontSize: 22, fontWeight: '700', color: '#F9FAFB' },
  headerSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  inputCard: {
    flexDirection: 'row',
    backgroundColor: '#1E293B',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#334155',
    overflow: 'hidden',
  },
  input: {
    flex: 1,
    color: '#F9FAFB',
    fontSize: 15,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  runButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
  },
  runButtonText: { color: '#fff', fontWeight: '700', fontSize: 14 },

  suggestionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  suggestionChip: {
    backgroundColor: '#1E293B',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#374151',
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  suggestionText: { color: '#9CA3AF', fontSize: 12 },

  errorCard: {
    backgroundColor: '#2D1B1B',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#EF4444',
    padding: 14,
  },
  errorText: { color: '#FCA5A5', fontSize: 14 },

  summaryBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  summaryPill: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    borderWidth: 1,
    padding: 8,
    minWidth: 60,
    alignItems: 'center',
  },
  summaryLabel: { color: '#6B7280', fontSize: 10, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  summaryValue: { fontSize: 13, fontWeight: '700', marginTop: 2 },

  card: {
    backgroundColor: '#1E293B',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#334155',
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#F9FAFB' },
  chevron: { color: '#6B7280', fontSize: 11 },
  cardBody: { paddingHorizontal: 14, paddingBottom: 14, borderTopWidth: 1, borderTopColor: '#334155' },

  kvRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  kvLabel: { color: '#6B7280', fontSize: 12, flex: 1 },
  kvValue: { color: '#E5E7EB', fontSize: 12, fontWeight: '600', flex: 2, textAlign: 'right' },

  emptyText: { color: '#6B7280', fontSize: 13, fontStyle: 'italic' },

  jsonBlock: {
    backgroundColor: '#0F172A',
    borderRadius: 6,
    padding: 10,
    marginTop: 4,
  },
  jsonText: { color: '#86EFAC', fontSize: 11, fontFamily: 'monospace' },

  resultRow: {
    flexDirection: 'row',
    gap: 10,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#1F2937',
  },
  resultIndex: { color: '#4B5563', fontSize: 13, fontWeight: '700', width: 24, marginTop: 2 },
  resultTopRow: { flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 },
  resultBook: { color: '#F9FAFB', fontSize: 12, fontWeight: '700' },
  resultOdds: { color: '#9CA3AF', fontSize: 12 },
  resultLegs: { color: '#8B5CF6', fontSize: 11, fontWeight: '600' },

  evGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  evCell: { alignItems: 'center', minWidth: 44 },
  evCellLabel: { color: '#4B5563', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4 },
  evCellValue: { fontSize: 12, fontWeight: '700', marginTop: 1 },
});
